import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ExperienceFilters } from '@/services/domain';
import { DEFAULT_EXPERIENCE_FILTERS } from './defaults';

interface ExperienceSearchState {
  filters: ExperienceFilters;
  update: (patch: Partial<ExperienceFilters>) => void;
  clear: () => void;
  /** True once the user ran a search (shows the summary bar on the experiences page). */
  searched: boolean;
  markSearched: () => void;
}

const Ctx = createContext<ExperienceSearchState | null>(null);

export function ExperienceSearchProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<ExperienceFilters>(DEFAULT_EXPERIENCE_FILTERS);
  const [searched, setSearched] = useState(false);
  const update = useCallback((patch: Partial<ExperienceFilters>) => setFilters((f) => ({ ...f, ...patch })), []);
  const clear = useCallback(() => {
    setFilters(DEFAULT_EXPERIENCE_FILTERS);
    setSearched(false);
  }, []);
  const markSearched = useCallback(() => setSearched(true), []);
  const value = useMemo(
    () => ({ filters, update, clear, searched, markSearched }),
    [filters, update, clear, searched, markSearched],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useExperienceSearch(): ExperienceSearchState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useExperienceSearch must be used inside <ExperienceSearchProvider>');
  return ctx;
}
